import { ChangeDetectionStrategy, Component, ContentChild, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { ConfirmDialogComponent } from '@portal-core/general/components/confirm-dialog/confirm-dialog.component';
import { FormBodyDirective } from '@portal-core/general/directives/form/form-body/form-body.directive';
import { FormFooterDirective } from '@portal-core/general/directives/form/form-footer/form-footer.directive';
import { FormHeaderDirective } from '@portal-core/general/directives/form/form-header/form-header.directive';
import { SnackBarErrorComponent } from '@portal-core/ui/snack-bar/components/snack-bar-error/snack-bar-error.component';
import { SnackBarAutoDismissDurationMS } from '@portal-core/ui/snack-bar/constants/snack-bar-auto-dismiss-duration.constant';
import { LoadingState } from '@portal-core/util/loading-state';
import { filter, Observable, switchMap } from 'rxjs';

export interface DeleteConfirmDialogData {
  title: string;
  prompt: string;
}

@Component({
  selector: 'mc-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormComponent {
  @Input() formGroup: FormGroup;

  // updating / creating
  @Input() submitFn: (form: FormGroup) => Observable<any>;
  @Input() errorMessage: string;
  @Input() successMessage: string;

  // deleting
  @Input() deleteFn: () => Observable<any>;
  @Input() deleteSuccessMessage: string;
  @Input() deleteConfirmDialogData: DeleteConfirmDialogData;

  @Input() loadingState: LoadingState<string> = null;

  @Output() success = new EventEmitter<void>();
  @Output() error = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();

  @ContentChild(FormHeaderDirective) formHeader: FormHeaderDirective;
  @ContentChild(FormBodyDirective) formBody: FormBodyDirective;
  @ContentChild(FormFooterDirective) formFooter: FormFooterDirective;

  savingState: LoadingState<string> = new LoadingState<string>();

  constructor(private errorService: ErrorService, private snackBar: MatSnackBar, private dialog: MatDialog) { }

  onDelete() {
    this.dialog.open(ConfirmDialogComponent, {
      width: '36rem',
      data: this.deleteConfirmDialogData

    }).afterClosed().pipe(
      filter(confirmed => confirmed),
      switchMap(() => this.deleteFn())
    ).subscribe({
      next: () => {
        this.formGroup.markAsPristine();
        this.savingState.update(false);
        this.success.emit();
        this.deleteSuccessMessage ? this.snackBar.open(this.deleteSuccessMessage, 'OK', { duration: SnackBarAutoDismissDurationMS }) : null;
      },
      error: error => {
        this.snackBar.openFromComponent(SnackBarErrorComponent, {
          data: this.errorService.getErrorMessages(error)[0]
        });
      }
    })

  }

  onSubmit() {
    if (!this.formGroup.valid) {
      return;
    }

    this.savingState.update(true);

    this.submitFn(this.formGroup).subscribe({
      next: () => {
        this.formGroup.markAsPristine();
        this.savingState.update(false);
        this.success.emit();
        this.successMessage ? this.snackBar.open(this.successMessage, 'OK', { duration: SnackBarAutoDismissDurationMS }) : null;
      },
      error: error => {
        this.savingState.update(false, this.errorMessage, this.errorService.getErrorMessages(error));
        this.error.emit(error);
      }
    });
  }

}
